/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback } from 'react';

import { jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';

import { useMenuControl } from '../../../context';
import {
	type NewMenuItemClickedAttributesV2,
	useUniversalCreateAnalytics,
} from '../../../hooks/use-universal-create-analytics';
import type { FoldableType } from '../../../types/menu';
import type { NativeMenuItem, RenderableMenuItem } from '../../../types/menuV2';
import { CrossAppItem } from '../CrossAppItem/CrossAppItem';
import MenuContentItem from '../MenuItem';
/**
 * Type guard to check if an item is a native menu item
 * @param item - The item to check
 * @returns true if the item is a native menu item, false otherwise
 */
export const isNativeRenderableMenuItem = (item: RenderableMenuItem): item is NativeMenuItem => {
	return 'children' in item && !('type' in item);
};

/**
 * Analytics props for MenuItemV2 component.
 * These props are used for analytics tracking related to menu item interactions.
 */
export interface MenuItemV2AnalyticsProps {
	/**
	 * Whether this item is in the "show more" section (hidden until show more is clicked)
	 */
	isInMore?: boolean;
	/**
	 * Index of this item in the list
	 */
	itemIndex: number;
	/**
	 * Foldable behaviour configuration of the section
	 */
	sectionFoldableBehaviour?: FoldableType;
	/**
	 * Count of items in the section
	 */
	sectionItemsCount: number;
	/**
	 * Index of the first item in the section
	 */
	sectionStartIndex: number;
	/**
	 * Total count of items in the menu
	 */
	totalMenuItemsCount: number;
}

/**
 * Props for MenuItemV2 component
 */
interface MenuItemV2Props extends MenuItemV2AnalyticsProps {
	/**
	 * The menu item to render (can be native or cross-app item)
	 */
	item: RenderableMenuItem;
}

/**
 * MenuItemV2 component that handles rendering of both native and cross-app menu items.
 *
 * For native items (type='native' or no type property), renders the existing MenuItem component.
 * For cross-app items (integration, iframe, link, platform), renders the CrossAppItem component.
 *
 * @param props - Component props
 * @returns Rendered menu item or null
 */
export default function MenuItemV2({
	item,
	itemIndex,
	totalMenuItemsCount,
	sectionStartIndex,
	sectionItemsCount,
	isInMore,
	sectionFoldableBehaviour,
}: MenuItemV2Props) {
	const { crossAppCreationConfig, product } = useMenuControl();
	const { fireNewMenuItemClicked } = useUniversalCreateAnalytics();

	const fireAnalytics = useCallback(
		(props: { isRightClick: boolean; itemType: NewMenuItemClickedAttributesV2['itemType'] }) => {
			if (fg('navx_uc_cross_app_creation_v2_enabled')) {
				const itemAti = isNativeRenderableMenuItem(item)
					? item.ati.toString()
					: item.ati || 'unknown';
				fireNewMenuItemClicked({
					ati: itemAti,
					itemType: props.itemType,
					sectionType: 'appDefinedSection',
					groupItemProduct: product,
					groupItemCount: sectionItemsCount,
					groupItemIndex: itemIndex,
					menuItemCount: totalMenuItemsCount,
					menuItemIndex: sectionStartIndex + itemIndex,
					isNativeItem: props.itemType === 'native',
					isRightClick: props.isRightClick,
					isInMore,
					sectionFoldableBehaviour,
				});
			}
		},
		[
			fireNewMenuItemClicked,
			item,
			itemIndex,
			totalMenuItemsCount,
			sectionStartIndex,
			sectionItemsCount,
			product,
			isInMore,
			sectionFoldableBehaviour,
		],
	);

	const menuItemWithAnalytics = useCallback(
		(item: RenderableMenuItem): RenderableMenuItem => {
			if (isNativeRenderableMenuItem(item)) {
				return {
					...item,
					onClick: (event: React.MouseEvent | React.KeyboardEvent) => {
						fireAnalytics({ isRightClick: false, itemType: 'native' });
						item.onClick?.(event);
					},
					onContextMenu: (event: React.MouseEvent) => {
						fireAnalytics({ isRightClick: true, itemType: 'native' });
						item.onContextMenu?.(event);
					},
				};
			}

			return item;
		},
		[fireAnalytics],
	);

	const handleCrossAppItemClickAnalytics = useCallback(
		(isRightClick: boolean) => {
			// Determine itemType dynamically based on whether item is native or cross-app
			const itemType = isNativeRenderableMenuItem(item) ? 'native' : item.type;
			fireAnalytics({ isRightClick, itemType });
		},
		[fireAnalytics, item],
	);

	const menuItem = menuItemWithAnalytics(item);

	// Check if this is a native menu item
	if (isNativeRenderableMenuItem(menuItem)) {
		// Render native menu item using existing MenuItem component
		return <MenuContentItem item={menuItem} />;
	}

	// For cross-app items, check if context exists and is enabled
	if (!crossAppCreationConfig.enabled) {
		return null;
	}

	// Render cross-app item with injected analytics
	const injectClickAnalytics = { onClick: handleCrossAppItemClickAnalytics };
	return (
		<CrossAppItem
			responseItem={menuItem}
			context={crossAppCreationConfig}
			product={product}
			injectClickAnalytics={injectClickAnalytics}
		/>
	);
}
